/**
 * 제목을 줄바꿈 단위(구)로 나누는 언어 — 낱말 사이를 띄우지 않아 브라우저가 낱말 한가운데서 줄을 바꾸는 일본어
 * (「プラットフォー/ム」)·크메르어(「សេវា/កម្ម」). 한국어는 사이트 뿌리의 `word-break: keep-all` 이 맡는다.
 */
export const TITLE_PHRASE_LANGUAGES = ["ja", "km"] as const;
export type TitlePhraseLanguage = (typeof TITLE_PHRASE_LANGUAGES)[number];

export const isTitlePhraseLanguage = (
  language: string | undefined
): language is TitlePhraseLanguage =>
  (TITLE_PHRASE_LANGUAGES as readonly string[]).includes(
    (language ?? "").split("-")[0]
  );

const WHITESPACE = /^[\s\u200B]+$/u;
const HIRAGANA_ONLY = /^[\p{Script=Hiragana}ー]+$/u;
const KATAKANA_START = /^[\p{Script=Katakana}ー]/u;
const KATAKANA_END = /[\p{Script=Katakana}ー]$/u;
/** 앞 구에 붙는 닫는 괄호·문장 부호 */
const TRAILING_MARK = /^[」』）)〉》】］〕”’、。，．,.!?！？・:：;；…〜~\-]+$/u;
/** 뒤 구에 붙는 여는 괄호 */
const OPENING_MARK = /^[「『（(〈《【［〔“‘]+$/u;
/** 뒤 낱말에 붙는 높임 접두(「ご用意」·「お問い合わせ」) */
const HONORIFIC_PREFIX = /^[おご御]$/u;
const LATIN_OR_DIGIT_START = /^[\p{Script=Latin}\d]/u;
/** 「1:1」·「2D」 처럼 이어지는 로마자·숫자 묶음 */
const LATIN_RUN_END = /[\p{Script=Latin}\d:./+&]$/u;

let japaneseSegmenter: Intl.Segmenter | undefined;
const cache = new Map<string, readonly string[]>();

/** 일본어 구에 이어 붙일지 — 조사·어미(히라가나), 이어진 가타카나(「ド|ローン|スクエア」), 문장 부호, 이어진 로마자·숫자 */
const joinsPreviousJapanese = (previous: string, segment: string) =>
  TRAILING_MARK.test(segment) ||
  (LATIN_RUN_END.test(previous) && LATIN_OR_DIGIT_START.test(segment)) ||
  (HIRAGANA_ONLY.test(segment) && !HONORIFIC_PREFIX.test(segment)) ||
  (KATAKANA_END.test(previous) && KATAKANA_START.test(segment));

/** 사전 분절(`Intl.Segmenter`)은 잘게 자르므로 조사·가타카나를 앞 구에 다시 붙여 문절에 가깝게 묶는다 */
const splitJapanese = (text: string) => {
  japaneseSegmenter ??= new Intl.Segmenter("ja", { granularity: "word" });
  const tokens: string[] = [];
  let joinNext = false;
  let afterSpace = true;
  for (const { segment } of japaneseSegmenter.segment(text)) {
    if (WHITESPACE.test(segment)) {
      tokens.push(segment);
      afterSpace = true;
      joinNext = false;
      continue;
    }
    const previous = afterSpace ? null : tokens[tokens.length - 1];
    const joinsPrevious =
      previous !== null &&
      (joinNext || joinsPreviousJapanese(previous, segment));
    if (joinsPrevious) {
      tokens[tokens.length - 1] = previous + segment;
    } else {
      tokens.push(segment);
    }
    joinNext = OPENING_MARK.test(segment) || HONORIFIC_PREFIX.test(segment);
    afterSpace = false;
  }
  return tokens;
};

/**
 * 크메르어는 띄어 쓴 자리만 줄바꿈 자리로 둔다 — 브라우저 사전은 합성어(「សេវា|កម្ម」)를 가르고,
 * 그 사전이 엔진마다 달라 낱말 단위로는 믿을 수 없다.
 */
const splitKhmer = (text: string) =>
  text.split(/([\s\u200B]+)/u).filter(Boolean);

/**
 * 제목 문구를 구와 공백으로 나눈다 — 공백 조각은 공백 그대로(줄바꿈 자리), 나머지는 그 안에서 줄을 바꾸지 않을 구.
 */
export function splitTitlePhrases(
  text: string,
  language: TitlePhraseLanguage
): readonly string[] {
  const cacheKey = `${language}\u0000${text}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;
  const tokens = language === "ja" ? splitJapanese(text) : splitKhmer(text);
  cache.set(cacheKey, tokens);
  return tokens;
}
